import type { ExperimentSignals } from '@@/api/attention/attention-items';
import type { Verdict } from '@@/api/attention/attention-items';
import { readVerdict } from '@@/api/attention/attention-items';

/**
 * When the scheduler may stop an experiment on its own.
 *
 * The dangerous half of scheduled stopping is significance, because stopping the
 * moment a test crosses p < 0.05 is textbook optional stopping. Peeking repeatedly
 * and stopping on the first crossing inflates the false positive rate far past the
 * nominal 5%, and an automated check is peeking every single minute, which is the
 * worst case of it. The corrected p-value in readVerdict is a reading aid for a human
 * who looks occasionally; it is not a valid stopping rule.
 *
 * SPRT is, because it is a sequential test with error rates controlled under
 * continuous monitoring. So the rule here is the conservative one: SPRT and the
 * corrected p-value must agree, on the same variant, in the same direction.
 *
 * The known consequence, which is why stopAt is mandatory alongside this:
 * sprt_result tests a fixed 5% target lift, so an experiment with a real 2% effect
 * leaves SPRT reading "no effect" while the corrected p-value eventually reads
 * significant. They never agree, nothing fires, and the experiment runs until its
 * scheduled end. That is the intended failure direction — significance can only ever
 * stop an experiment early, never be the only thing that stops it.
 */

/** A full business cycle, twice. Weekday and weekend traffic do not convert alike. */
export const DEFAULT_MIN_RUNTIME_MS = 14 * 24 * 60 * 60 * 1000;

/** Below one full week, a stop is measuring a Tuesday rather than a business. */
export const MIN_ALLOWED_RUNTIME_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * What SPRT must say for each reading of the corrected p-value.
 *
 * readVerdict already returns the figures for the variant it chose, and those figures
 * carry that variant's own sprtResult, so agreement is checked on one arm rather than
 * across two independently-picked ones.
 */
const SPRT_AGREEMENT: Record<Verdict['kind'], string | null> = {
  'significant-positive': 'Variant significant',
  'significant-negative': 'Variant significant',
  'no-effect': 'Variant shows no effect',
  interaction: null,
  srm: null,
  'no-data': null,
  overdue: null,
  'needs-implementation': null,
  'deployed-not-implemented': null,
  'needs-report': null,
  'next-by-pxl': null,
};

/**
 * Mirrors what checkSampleRatio can return.
 *
 * Only 'mismatch' blocks. 'not-applicable' means the check could not run at all —
 * fewer than two arms receiving traffic, too few sessions, or no weights configured —
 * which is an absence of evidence, not evidence of a problem. Treating it as a
 * failure would stop every experiment from concluding in its first days.
 */
export type SrmState = 'ok' | 'mismatch' | 'unknown' | 'not-applicable';

export type StopReason = 'time' | 'significance';

export type StopDecision =
  | { stop: false; blocked: boolean; reason: string }
  | { stop: true; reason: StopReason; detail: string; verdict: Verdict | null };

export type StoppingRuleParams = {
  now: number;
  /** experiments.startsAt — when it actually went live, not when it was scheduled to. */
  startedAt: number | null;
  stopAt: number | null;
  stopOnSignificance: boolean;
  minRuntimeMs: number | null;
  signals: ExperimentSignals | null;
  /** From checkSampleRatio, so the engine and the statistics screen cannot disagree. */
  srmState: SrmState;
  /** False when the experiment has recorded no traffic for a full day. */
  hasRecentTraffic: boolean;
  /**
   * Whether another running experiment is interfering with this one.
   *
   * A separate gate from srmState rather than folded into it, because the two do not
   * overlap the way they look like they should. A redirect interaction does surface as a
   * mismatch, but two experiments editing the same element produce a perfect split and a
   * page combination nobody QA'd — invisible to the sample-ratio check, and still not a
   * reading worth stopping an experiment over.
   */
  hasInteraction: boolean;
};

export const effectiveMinRuntime = (minRuntimeMs: number | null): number =>
  Math.max(minRuntimeMs ?? DEFAULT_MIN_RUNTIME_MS, MIN_ALLOWED_RUNTIME_MS);

export const sprtAgrees = (verdict: Verdict): boolean => SPRT_AGREEMENT[verdict.kind] === verdict.figures.sprtResult;

/**
 * The time stop is checked first and is not gated on trust.
 *
 * A broken experiment reaching its scheduled end should still end: stopping is always
 * safe, and leaving an experiment running past its planned end because its traffic
 * split is wrong compounds one problem with another. Trust only gates the
 * significance path, where a bad reading would be the entire basis for the action.
 */
export function evaluateStop(params: StoppingRuleParams): StopDecision {
  const { now, startedAt, stopAt, stopOnSignificance, minRuntimeMs, signals, srmState, hasRecentTraffic } = params;

  if (stopAt !== null && now >= stopAt) {
    return { stop: true, reason: 'time', detail: 'Reached its scheduled end time.', verdict: null };
  }

  if (!stopOnSignificance) return { stop: false, blocked: false, reason: 'No significance stop configured.' };

  if (srmState === 'mismatch') {
    return { stop: false, blocked: true, reason: 'Sample ratio mismatch: the result cannot be trusted.' };
  }

  if (params.hasInteraction) {
    return { stop: false, blocked: true, reason: 'Another running experiment is interfering with this one.' };
  }

  if (startedAt === null) return { stop: false, blocked: false, reason: 'Not started yet.' };

  /**
   * Checked before the no-traffic gate below, not after it. VisitorCountHourly only
   * gets rows once real visitors arrive and the per-minute tracking cron aggregates
   * them, so a freshly-started experiment looks identical to one that has recorded no
   * traffic for a full day — indistinguishable until it has actually had the chance to
   * receive any. Blocking on that reading is terminal (the scheduler's tick only
   * re-selects armed/held/active rows, never blocked ones), so evaluating it before an
   * experiment has been running long enough to have produced traffic would self-destruct
   * every stop-on-significance schedule within a minute of going live.
   */
  const minRuntime = effectiveMinRuntime(minRuntimeMs);
  if (now - startedAt < minRuntime) {
    const days = Math.ceil((minRuntime - (now - startedAt)) / (24 * 60 * 60 * 1000));
    return { stop: false, blocked: false, reason: `Minimum runtime not reached, ${days} day(s) remaining.` };
  }

  if (!hasRecentTraffic) {
    return { stop: false, blocked: true, reason: 'No traffic recorded in the last day.' };
  }

  if (!signals) return { stop: false, blocked: false, reason: 'No statistics available yet.' };

  // readVerdict applies the shared gates: every arm above 1000 sessions, and a lift
  // interval that does not span zero. Reusing it is what keeps this consistent with
  // what the dashboard tells a reader about the same experiment.
  const verdict = readVerdict(signals);
  if (!verdict) return { stop: false, blocked: false, reason: 'No conclusive reading yet.' };

  if (!sprtAgrees(verdict)) {
    return {
      stop: false,
      blocked: false,
      reason: `SPRT and the corrected p-value disagree (${verdict.figures.sprtResult ?? 'no SPRT result'} vs ${verdict.kind}).`,
    };
  }

  return {
    stop: true,
    reason: 'significance',
    detail: `SPRT and the corrected p-value agree: ${verdict.kind}.`,
    verdict,
  };
}
